import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePlan } from '../context/PlanContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  CircleNotch,
  CurrencyDollar,
  FloppyDisk,
  Link as LinkIcon,
  PencilSimple,
  Target,
  Trophy,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const ConsultantAssessmentDetail = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { planLimits } = usePlan();
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [scanData, setScanData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [wizardResponses, setWizardResponses] = useState([]);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    consultant_notes: '',
    priority_score: 0
  });
  const [saving, setSaving] = useState(false);

  const fetchScanDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessment/admin/scans/${assessmentId}`,
        {},
        activeOrganization?.organization_id
      );

      if (!response.ok) {
        throw new Error('Failed to fetch assessment details');
      }

      const data = await response.json();
      setScanData(data.scan);
      setOpportunities(data.opportunities || []);
      setWizardResponses(data.wizard_responses?.responses || []);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      alert('Failed to load assessment details');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, activeOrganization]);

  useEffect(() => {
    // Check consultant access
    if (!planLimits.is_consultant) {
      navigate('/');
      return;
    }

    if (activeOrganization && assessmentId) {
      fetchScanDetail();
    }
  }, [assessmentId, activeOrganization, planLimits.is_consultant, navigate, fetchScanDetail]);

  const handleEditOpportunity = (opportunity) => {
    setEditingOpportunity(opportunity.id);
    setEditForm({
      status: opportunity.status || 'identified',
      consultant_notes: opportunity.consultant_notes || '',
      priority_score: opportunity.priority_score || 0
    });
  };

  const handleSaveOpportunity = async (opportunityId) => {
    setSaving(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessment/opportunities/${opportunityId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editForm)
        },
        activeOrganization?.organization_id
      );

      if (!response.ok) {
        throw new Error('Failed to update opportunity');
      }

      // Update local state
      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? { ...opp, ...editForm }
            : opp
        )
      );

      setEditingOpportunity(null);
      setEditForm({ status: '', consultant_notes: '', priority_score: 0 });
    } catch (error) {
      console.error('Error updating opportunity:', error);
      alert('Failed to update opportunity. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'text-[#14F195]',
      'B': 'text-[#6366F1]',
      'C': 'text-[#F59E0B]',
      'D': 'text-[#EF4444]'
    };
    return colors[grade] || 'text-[#A0AEC0]';
  };

  const getStatusColor = (status) => {
    const colors = {
      'identified': 'bg-[#A0AEC0]/20 text-[#A0AEC0]',
      'in_progress': 'bg-[#6366F1]/20 text-[#6366F1]',
      'completed': 'bg-[#14F195]/20 text-[#14F195]',
      'on_hold': 'bg-[#F59E0B]/20 text-[#F59E0B]'
    };
    return colors[status] || colors.identified;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'identified': 'Identified',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'on_hold': 'On Hold'
    };
    return labels[status] || 'Identified';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  if (!scanData) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Assessment not found</p>
          <button
            onClick={() => navigate('/consultant/scans')}
            className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/consultant/scans')}
            className="flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl font-bold text-white mb-2">
              {scanData.company_name} <span className="gradient-text">Assessment Details</span>
            </h1>
            <p className="text-[#A0AEC0]">
              {scanData.industry} • Created {new Date(scanData.created_at).toLocaleDateString()}
            </p>
          </motion.div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#14F195]/20 to-[#6366F1]/20 flex items-center justify-center">
                  <Trophy size={24} className="text-[#14F195]" />
                </div>
                <div className={`text-3xl font-bold ${getGradeColor(scanData.readiness_grade)}`}>
                  {scanData.readiness_grade || 'B'}
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">AI Readiness Grade</p>
              <div className="mt-3 w-full h-2 bg-[#1A1B23] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#6366F1] to-[#14F195]"
                  style={{ width: `${scanData.ai_readiness_score || 75}%` }}
                ></div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center">
                  <CurrencyDollar size={24} className="text-[#6366F1]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Annual Savings</p>
              <p className="text-3xl font-bold text-white">
                ${scanData.total_savings_annual?.toLocaleString() || '0'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EC4899]/20 to-[#8B5CF6]/20 flex items-center justify-center">
                  <Target size={24} className="text-[#EC4899]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Opportunities</p>
              <p className="text-3xl font-bold text-white">{opportunities.length}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-[#EF4444]/20 flex items-center justify-center">
                  <Calendar size={24} className="text-[#F59E0B]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Status</p>
              <p className="text-lg font-bold text-white capitalize">{scanData.status}</p>
            </motion.div>
          </div>

          {/* Wizard Responses */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-2xl p-8 mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Wizard Responses</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {wizardResponses.map((response, index) => (
                <div key={index} className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)]">
                  <p className="text-sm font-semibold text-[#6366F1] mb-2">{response.question_text}</p>
                  <p className="text-white">
                    {typeof response.answer === 'object' 
                      ? JSON.stringify(response.answer, null, 2)
                      : response.answer
                    }
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Opportunities List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-bold text-white mb-6">Opportunities</h2>
            <div className="space-y-4">
              {opportunities.map((opportunity, index) => (
                <div key={opportunity.id} className="glass rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{opportunity.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(opportunity.status || 'identified')}`}>
                          {getStatusLabel(opportunity.status || 'identified')}
                        </span>
                        {opportunity.process_id && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#14F195]/20 text-[#14F195] flex items-center gap-1">
                            <LinkIcon size={12} />
                            Linked to Process
                          </span>
                        )}
                      </div>
                      <p className="text-[#A0AEC0] mb-4">{opportunity.description}</p>

                      {/* Editable Fields */}
                      {editingOpportunity === opportunity.id ? (
                        <div className="space-y-4 mt-4 p-4 rounded-xl bg-[rgba(99,102,241,0.1)] border border-[#6366F1]/30">
                          <div>
                            <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Status</label>
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                              className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                            >
                              <option value="identified">Identified</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="on_hold">On Hold</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Priority Score</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={editForm.priority_score}
                              onChange={(e) => setEditForm({ ...editForm, priority_score: parseInt(e.target.value) || 0 })}
                              className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Consultant Notes</label>
                            <textarea
                              value={editForm.consultant_notes}
                              onChange={(e) => setEditForm({ ...editForm, consultant_notes: e.target.value })}
                              rows={4}
                              className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                              placeholder="Add your notes here..."
                            />
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveOpportunity(opportunity.id)}
                              disabled={saving}
                              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#14F195] to-[#6366F1] text-white font-medium transition-all disabled:opacity-50"
                            >
                              {saving ? (
                                <>
                                  <CircleNotch size={16} className="animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <FloppyDisk size={16} />
                                  FloppyDisk
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => setEditingOpportunity(null)}
                              disabled={saving}
                              className="flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all disabled:opacity-50"
                            >
                              <X size={16} />
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {opportunity.consultant_notes && (
                            <div className="mt-4 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)]">
                              <p className="text-sm font-semibold text-[#6366F1] mb-1">Consultant Notes:</p>
                              <p className="text-white text-sm">{opportunity.consultant_notes}</p>
                            </div>
                          )}

                          <button
                            onClick={() => handleEditOpportunity(opportunity)}
                            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all"
                          >
                            <PencilSimple size={16} />
                            PencilSimple Status & Notes
                          </button>
                        </>
                      )}
                    </div>

                    <div className="ml-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center">
                        <span className="text-2xl font-bold gradient-text">
                          {opportunity.priority_score}
                        </span>
                      </div>
                      <p className="text-xs text-center text-[#718096] mt-2">Priority</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConsultantAssessmentDetail;
